import React, { FC, useEffect, useState } from 'react';
import { BlackcartContext } from './BlackCartContext';
import { IBlackcartProps } from '../types';
import { BaseBlackcart, BlackcartError, IValidatorCart } from '@blackcart/base';

const BlackcartProvider: FC<IBlackcartProps> = (props) => {
  const {
    shopDomain,
    selectedCurrency,
    cart,
    modalConfig,
    useModal,
    clearCart,
    moneyFormatter,
    isProduction,
    children
  } = props;

  const [blackcart, setBCInstance] = useState<BaseBlackcart>();
  const [storefrontCart, setStorefrontCart] = useState<IValidatorCart>(cart);

  const [isInitializing, setInitializing] = useState<boolean>(true);

  if (!shopDomain || !selectedCurrency || !cart || !clearCart) {
    return <>{children}</>;
  }

  useEffect(() => {
    initBC({
      shopDomain,
      cart,
      clearCart,
      selectedCurrency,
      modalConfig,
      useModal,
      isProduction,
      moneyFormatter
    });
  }, []);

  const initBC = ({
    shopDomain,
    cart,
    clearCart,
    selectedCurrency,
    isProduction,
    useModal,
    modalConfig,
    moneyFormatter
  }: IBlackcartProps) => {
    setInitializing(true);

    BaseBlackcart.getInstance(
      shopDomain,
      cart,
      clearCart,
      selectedCurrency,
      isProduction,
      useModal,
      modalConfig,
      moneyFormatter
    )
      .then((bc) => setBCInstance(bc))
      .catch((error) => {
        if (error instanceof BlackcartError) {
          console.error('Blackcart initialization error: ', error?.status, error?.message);
        } else {
          console.error('Blackcart initialization error: ', error);
        }
        setBCInstance(undefined);
      })
      .finally(() => setInitializing(false));
  };

  const handleCurrencyChange = (currency: string) => {
    initBC({
      selectedCurrency: currency,
      cart: storefrontCart,
      shopDomain,
      isProduction,
      useModal,
      modalConfig,
      clearCart,
      moneyFormatter
    });
  };

  const handleCartChange = (cart: IValidatorCart) => {
    if (!blackcart) {
      return;
    }
    setStorefrontCart(cart);
    blackcart.setCart(cart);
  };

  return (
    <BlackcartContext.Provider
      value={{
        baseBlackcart: blackcart,
        shopDomain,
        selectedCurrency,
        cart,
        modalConfig,
        useModal,
        clearCart,
        moneyFormatter,
        isProduction,
        isInitializing,
        setCart: handleCartChange,
        isTBYBItem: blackcart?.isTBYBItem || (() => false),
        deposit: blackcart?.deposit || null,
        payTodayTotal: blackcart?.payTodayTotal || null,
        cartViolations: () => blackcart?.cartInfo?.warning || null,
        isTBYBQualifiedCart: () => blackcart?.isTBYBQualifiedCart || false,
        setCurrency: handleCurrencyChange,
        showBlackcartModal: blackcart?.showBlackcartModal || (() => {}),
        goToBlackcartCheckout: blackcart?.goToBlackcartCheckout || (() => {}),
        bcCart: blackcart?.validatedCart
      }}
    >
      {children}
    </BlackcartContext.Provider>
  );
};

export { BlackcartProvider };
